/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { RightOutlined } from '@ant-design/icons'
import { useHistory } from 'react-router-dom'
import { ROUTES } from '@sportninja/common/constants/app'
import colors from '@sportninja/common/constants/appColors'
import { pendingInvitationsCopy } from '@sportninja/common/features/invitation/constants/pendingInvitationsCopy'
import {
  describePendingInvitations,
  toPendingInvitationRow,
} from '@sportninja/common/features/invitation/helper/pendingInvitations'
import { usePendingInvitations } from '@sportninja/common/features/invitation/hooks/usePendingInvitations'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import { useNotificationsPanel } from 'src/components/NotificationsPanelContext'
import NewReleasesIcon from 'src/components/icons/NewReleasesIcon'
import Text from 'src/components/ts-components/Text'
import { useSelector } from 'src/hooks/useRedux'

interface PendingInvitationsCardProps {
  userId: string | null | undefined
}

/**
 * SN-7849 (B) — web Home's twin of mobile's pending-invitations card, shown
 * whenever an invitation is waiting for the signed-in person (never on a page
 * framed in another site). One invitation opens it; several open the bell on
 * its Invitations tab.
 */
const PendingInvitationsCard = ({ userId }: PendingInvitationsCardProps) => {
  'use memo'

  const history = useHistory()
  const panel = useNotificationsPanel()
  const inFrame = useSelector(
    (state: { auth: { inFrame: boolean } }) => state.auth.inFrame
  )
  const { pendingInvitations } = usePendingInvitations(userId)

  if (inFrame || pendingInvitations.length === 0) {
    return null
  }

  const rows = pendingInvitations.map(toPendingInvitationRow)

  const openInvitations = () => {
    if (rows.length === 1) {
      history.push(`${ROUTES.INVITE_V2_ROOT}${rows[0].id}`)
      return
    }
    panel.open('invitations')
  }

  return (
    <button type='button' css={cardStyle} onClick={openInvitations}>
      <NewReleasesIcon size={24} color={colors.PRIMARY} />
      <span css={copyStyle}>
        <Text variant='heading6' weight='semiBold' color={colors.WHITE}>
          {capitalizeWords(pendingInvitationsCopy.cardTitle(rows.length))}
        </Text>
        <Text variant='paragraphSmall' weight='regular' color={colors.WHITE}>
          {describePendingInvitations(rows)}
        </Text>
      </span>
      <RightOutlined css={chevronStyle} />
    </button>
  )
}

export default PendingInvitationsCard

const cardStyle = css`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 12px 16px;
  border: 1px solid ${colors.PRIMARY};
  border-radius: 8px;
  background-color: transparent;
  text-align: left;
  cursor: pointer;
  transition: opacity 0.15s ease;

  &:hover {
    opacity: 0.85;
  }

  &:focus-visible {
    outline: 2px solid ${colors.PRIMARY};
    outline-offset: 2px;
  }
`

const copyStyle = css`
  display: flex;
  flex: 1;
  flex-direction: column;
  min-width: 0;
`

const chevronStyle = css`
  color: ${colors.PRIMARY};
  font-size: 16px;
`
